import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";
import PublicHeader from "./PublicHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailConfirmation({ onGoToLogin }) {

  const {
    pendingEmail,
    confirmEmail,
    authError,
    setAuthError,
    setPendingConfirmation,
  } = useAuth();
  const [token, setToken] = useState("");
  const [resendMsg, setResendMsg] = useState("");
  const [autoSubmitting, setAutoSubmitting] = useState(false);
  const navigate = useNavigate();

  // Auto-confirm if token present in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      setToken(urlToken);
      setAutoSubmitting(true);
      handleConfirm(urlToken);
    }
    // eslint-disable-next-line
  }, []);

  // Confirm email (from form or auto)
  const handleConfirm = async (passedToken) => {
    setAuthError("");
    setResendMsg("");
    const useToken = passedToken || token;
    // --- BEGIN PATCH: Get reset_token from response ---
    let response;
    try {
      response = await confirmEmail(useToken);
    } catch {
      response = null;
    }
    if (response && (response.ok || response.message)) {
      setPendingConfirmation(false);
      const resetToken =
        response.reset_token ||
        (typeof response.data === "object" && response.data.reset_token) ||
        useToken;
      // Always use the reset_token from backend if present
      setTimeout(() => {
        navigate(`/reset-password?token=${resetToken}`);
      }, 500);
    }
    setAutoSubmitting(false);
  };

  // Manual confirm (button)
  const handleSubmit = (e) => {
    e.preventDefault();
    handleConfirm();
  };

  // Resend code logic (unchanged)
  const handleResend = async () => {
    setResendMsg("");
    setAuthError("");
    try {
      const res = await fetch(`${BACKEND_URL}/resend-confirmation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: pendingEmail }),
      });
      const data = await res.json();
      if (res.ok) setResendMsg("Confirmation email sent!");
      else setResendMsg(data.detail || "Failed to resend.");
    } catch (err) {
      setResendMsg("Network error.");
    }
  };
  return (
    <div className="mx-auto px-4" data-testid="email-confirmation-page">
      <PublicHeader />

      {/* Confirmation Card */}
      <div className="max-w-md mx-auto p-6 bg-white rounded-2xl">
        <h2 className="text-2xl font-bold mb-2 text-center" data-testid="email-confirmation-heading">
          Confirm Your E-mail Address
        </h2>

        <div className="mb-4 text-center text-gray-700 leading-relaxed">
          We&apos;ve sent a confirmation e-mail to:
          <br />
          <b data-testid="email-confirmation-pending-email">{pendingEmail}</b>.
          <br />
          <br />
          Please paste the token here:
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            className="w-full border p-2 rounded"
            placeholder="Confirmation token"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={autoSubmitting}
            data-testid="email-confirmation-token"
          />

          {authError && (
            <div className="text-red-600 text-center font-semibold" aria-live="polite" data-testid="email-confirmation-error">
              {authError}
            </div>
          )}

          <button
            className="text-white px-4 py-2 rounded w-full hover:opacity-90 font-bold"
            style={{ backgroundColor: "#00a7e1" }}
            type="submit"
            disabled={autoSubmitting}
            data-testid="email-confirmation-submit"
          >
            {autoSubmitting ? "Confirming..." : "Confirm E-mail"}
          </button>
        </form>

        <div className="mb-2 mt-4 text-center">
          Didn&apos;t get the code?{" "}
          <button
            className="underline hover:opacity-90 font-semibold"
            style={{ color: "#00a7e1" }}
            type="button"
            onClick={handleResend}
            data-testid="email-confirmation-resend"
          >
            Resend
          </button>
        </div>

        {resendMsg && (
          <div className="mt-2 text-center font-semibold" style={{ color: "#94c83d" }} aria-live="polite" data-testid="email-confirmation-resend-message">
            {resendMsg}
          </div>
        )}
      </div>
    </div>
  );
}
